import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

import { isCapability, type Capability } from '../model/capabilities';
import { AUTH_CONFIG } from './auth.config';
import type { PartnerSummary, RolesTokenPayload, SessionUser } from './session.types';

/** The non-httpOnly cookie carrying the readable session claims. */
const ROLES_TOKEN_COOKIE = 'roles_token';

/**
 * Reactive, signal-based view of the shared session, decoded from the readable
 * `roles_token` cookie. This is the **coarse altitude** of the two-altitudes
 * model (design doc §6): it answers "who is this and do they have partner
 * access at all?" for surfacing entry points and gating coarse navigation.
 *
 * **UI gating only** — the backend `CapabilitiesGuard` is the real enforcement,
 * and fine-grained per-capability checks come from the fresh
 * `GET /v1/me/capabilities` via `CapabilitiesService` (fp1-04), not from here.
 *
 * Cookies emit no change event, so the decoded state is read once at
 * construction and re-read explicitly via {@link refresh} (call it after a
 * login redirect or a token refresh).
 *
 * The httpOnly `access_token` / `refresh_token` cookies are backend-only and
 * are never read here.
 *
 * **Accepted risk, by design.** The claims below are decoded, never
 * signature-verified, and `roles_token` is deliberately *not* httpOnly so the
 * UI can read them. Two consequences worth naming: a user can hand-edit their
 * own cookie and change what this service reports, and any XSS on any
 * `*.halio.ai` surface can read the session claims. Neither grants access —
 * the backend `CapabilitiesGuard` is the only enforcement, and the httpOnly
 * tokens that actually authenticate a request are out of reach here. Do not
 * mistake a decoded claim for a trust boundary.
 *
 * TODO(fp1-03/p2-05): if the `roles_token` ever turns out not to be readable on
 * a subdomain (it is set `Domain=.halio.ai`, so it should be), add an API
 * fallback that fetches session state. Deferred — no `HttpClient` dependency
 * yet, to keep this service framework-only and trivially testable.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly document = inject(DOCUMENT);
  private readonly config = inject(AUTH_CONFIG);

  private readonly payload = signal<RolesTokenPayload | null>(readSession(this.document));

  /**
   * The decoded payload, but only while it is still valid — an expired token
   * reads as logged-out across every derived signal below.
   */
  private readonly activePayload = computed<RolesTokenPayload | null>(() => {
    const payload = this.payload();
    if (!payload) {
      return null;
    }
    return payload.exp * 1000 > Date.now() ? payload : null;
  });

  /** True when a valid, unexpired payload is present. */
  readonly isLoggedIn = computed(() => this.activePayload() !== null);

  /** The user's role claim, or `null` when logged out. */
  readonly role = computed(() => this.activePayload()?.role ?? null);

  /** The user id (`sub` claim), or `null` when logged out. */
  readonly userId = computed(() => this.activePayload()?.sub ?? null);

  /** Lightweight session identity from token claims — not the full profile. */
  readonly user = computed<SessionUser | null>(() => {
    const payload = this.activePayload();
    if (!payload) {
      return null;
    }
    return {
      id: payload.sub,
      role: payload.role,
      isOnboarding: payload.isOnboarding ?? false,
      stripeStatus: payload.stripeStatus ?? null,
    };
  });

  /** Per-partner access summary (UI hint only); `[]` when absent. */
  readonly partnerAccess = computed<PartnerSummary[]>(
    () => this.activePayload()?.partnerAccess ?? [],
  );

  /** Re-read the `roles_token` cookie into the reactive state. */
  refresh(): void {
    this.payload.set(readSession(this.document));
  }

  /**
   * Reset local session state to logged-out. Clearing the actual cookies
   * (httpOnly and `roles_token`) is the backend's job on logout; this only
   * drops the in-memory decoded state.
   */
  clear(): void {
    this.payload.set(null);
  }

  /**
   * Full-page navigate to the configured shared login, preserving where to
   * return to. Uses `location.assign` (not the `Router`) because the login can
   * live on another origin (`app.halio.ai`) which the router cannot reach.
   *
   * The default return target is the current **path** (plus hash), not the full
   * `href`: the login is on another origin, so anything included here crosses
   * into that origin's query string, its access logs and its `Referer` header.
   * A caller that genuinely needs a query param carried across passes an
   * explicit `returnUrl`.
   */
  redirectToLogin(returnUrl?: string): void {
    const location = this.document.defaultView?.location;
    if (!location) {
      return;
    }
    const target = returnUrl ?? `${location.origin}${location.pathname}${location.hash}`;
    const separator = this.config.loginUrl.includes('?') ? '&' : '?';
    location.assign(`${this.config.loginUrl}${separator}returnUrl=${encodeURIComponent(target)}`);
  }
}

/** Read and decode the `roles_token` cookie; `null` on absent/malformed. */
function readSession(doc: Document): RolesTokenPayload | null {
  const raw = getCookie(doc, ROLES_TOKEN_COOKIE);
  if (!raw) {
    return null;
  }
  try {
    const decoded = jwtDecode<RolesTokenPayload>(raw);
    if (!decoded || typeof decoded.exp !== 'number') {
      return null;
    }
    return { ...decoded, partnerAccess: sanitisePartnerAccess(decoded.partnerAccess) };
  } catch {
    // Malformed token — treat as logged out rather than throwing.
    return null;
  }
}

/** Port of the legacy cookie read: find one cookie value by name. */
function getCookie(doc: Document, name: string): string | null {
  const cookies = doc.cookie ? doc.cookie.split(';') : [];
  for (const cookie of cookies) {
    const [cookieName, ...rest] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return null;
}

/**
 * Keep only well-formed partner summaries, dropping any capability strings not
 * in the fp1-01 catalogue so a newer backend catalogue never breaks decoding.
 */
function sanitisePartnerAccess(
  summaries: PartnerSummary[] | undefined,
): PartnerSummary[] | undefined {
  if (!Array.isArray(summaries)) {
    return undefined;
  }
  return summaries
    .filter(
      (summary): summary is PartnerSummary => !!summary && typeof summary.partnerId === 'string',
    )
    .map((summary) => ({
      partnerId: summary.partnerId,
      caps: Array.isArray(summary.caps)
        ? summary.caps.filter((cap): cap is Capability => isCapability(cap))
        : [],
      // Rebuilt field by field, so anything not named here is dropped — which is
      // why this line exists rather than a spread. Compared against `true`, not
      // coerced: a token carrying any other value must not produce a marker.
      ...(summary.suspended === true && { suspended: true as const }),
    }));
}
